import { Box, CircularProgress, Paper, Typography } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"

import type { ProductShelvingRequirement } from "../../types"

import ProductShelvingRequirementFormDialog from "./ProductShelvingRequirementFormDialog"
import ProductShelvingRequirementTable from "./ProductShelvingRequirementTable"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import PermissionContentWrapper from "@/components/PermissionWrapper/PermissionContentWrapper"
import { PermissionActionType } from "@/constants/permissions"

interface ProductShelvingRequirementSectionProps {
  productId: number
  shelvingRequirements: ProductShelvingRequirement | null
  isFetching: boolean
  onRefresh: () => void
}

const ProductShelvingRequirementSection: FC<ProductShelvingRequirementSectionProps> = ({
  productId,
  shelvingRequirements,
  isFetching,
  onRefresh,
}) => {
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false)

  const handleFormDialogOpen = () => setIsFormDialogOpen(true)

  const handleFormDialogSubmit = () => {
    onRefresh()
    setIsFormDialogOpen(false)
  }

  const handleFormDialogClose = () => setIsFormDialogOpen(false)

  return (
    <Paper
      variant='outlined'
      elevation={0}
      className='flex flex-col gap-y-6 border-gray-400 p-6'
      data-testid='product-shelving-requirement'
    >
      <Typography variant='subtitle1' component='h2' className='text-ml'>
        Shelving Requirements
      </Typography>
      {!shelvingRequirements && isFetching && (
        <Box className='flex w-full justify-center'>
          <CircularProgress />
        </Box>
      )}
      {!shelvingRequirements && !isFetching && (
        <PermissionContentWrapper permissions={[PermissionActionType.WAREHOUSES_VIEW]}>
          <InformationMissingAlert
            title='Add Shelving Requirements'
            action={
              <Button
                color='inherit'
                size='small'
                appearance='outlined'
                startIcon={<AddOutlined />}
                onClick={handleFormDialogOpen}
              >
                Shelving Requirements
              </Button>
            }
          />
        </PermissionContentWrapper>
      )}
      {shelvingRequirements && (
        <ProductShelvingRequirementTable shelvingRequirement={shelvingRequirements} onEdit={handleFormDialogOpen} />
      )}
      {isFormDialogOpen && (
        <ProductShelvingRequirementFormDialog
          productId={productId}
          shelvingRequirement={shelvingRequirements}
          onClose={handleFormDialogClose}
          onSubmit={handleFormDialogSubmit}
        />
      )}
    </Paper>
  )
}

export default ProductShelvingRequirementSection
